import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  Platform,
  Animated,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { PanGestureHandler, State } from 'react-native-gesture-handler';
import { colors } from '../constants/colors';
import ClassEditModal from '../components/ClassEditModal';
import { TimeSlot, TimetableData } from '../types/timetable';
import { useTimetableStore } from '../store/timetableStore';
import { checkNotificationPermissions } from '../services/notifications';
import { useNavigation } from '@react-navigation/native';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const { width: screenWidth } = Dimensions.get('window');
const PADDING = 20;
const CONTAINER_WIDTH = screenWidth - (PADDING * 2);
const TAB_WIDTH = CONTAINER_WIDTH / DAYS.length;

interface TimetableScreenProps {
  route?: {
    params?: {
      initialDay?: string;
    };
  };
}

const TimetableScreen: React.FC<TimetableScreenProps> = ({ route }) => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { timetable, loading, initialize, addClass, deleteClass } = useTimetableStore();
  const [selectedDay, setSelectedDay] = useState(route?.params?.initialDay || DAYS[0]);
  const [tabPosition] = useState(new Animated.Value(DAYS.indexOf(route?.params?.initialDay || DAYS[0]) * (CONTAINER_WIDTH / DAYS.length)));
  const [showEditModal, setShowEditModal] = useState(false);

  // Animation references
  const listItemAnimations = useRef<{ [key: string]: Animated.Value }>([]);
  const translateXRefs = useRef<{ [key: string]: Animated.Value }>([]);
  const itemHeightRefs = useRef<{ [key: string]: Animated.Value }>([]);
  const opacityRefs = useRef<{ [key: string]: Animated.Value }>([]);

  // Initialize animations for new classes
  useEffect(() => {
    if (!timetable || !selectedDay) return;

    timetable[selectedDay]?.forEach((slot) => {
      if (!listItemAnimations.current[slot.id]) {
        listItemAnimations.current[slot.id] = new Animated.Value(1);
        translateXRefs.current[slot.id] = new Animated.Value(0);
        itemHeightRefs.current[slot.id] = new Animated.Value(70);
        opacityRefs.current[slot.id] = new Animated.Value(1);
      }
    });
  }, [timetable, selectedDay]);

  // Load saved timetable data on mount
  useEffect(() => {
    initialize();
  }, []);

  const onDaySelect = (day: string, index: number) => {
    setSelectedDay(day);
    Animated.spring(tabPosition, {
      toValue: index * (CONTAINER_WIDTH / DAYS.length),
      useNativeDriver: true,
      bounciness: 8
    }).start();
  };

  const handleAddClass = () => {
    setShowEditModal(true);
  };

  const handleSaveClass = async (classData: TimeSlot) => {
    try {
      const hasPermissions = await checkNotificationPermissions();
      if (!hasPermissions) {
        Alert.alert('Warning', 'Notifications are disabled. You won\'t receive reminders for your classes.');
      }

      await addClass(selectedDay, classData);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error saving class:', error);
      Alert.alert('Error', 'Failed to save class. Please try again.');
    }
  };

  const handleDeleteClass = async (classId: string) => {
    try {
      await deleteClass(selectedDay, classId);
      // Clean up animation refs after deletion
      delete listItemAnimations.current[classId];
      delete translateXRefs.current[classId];
      delete itemHeightRefs.current[classId];
      delete opacityRefs.current[classId];
    } catch (error) {
      console.error('Error deleting class:', error);
    }
  };

  const onGestureEvent = (id: string) => {
    if (!translateXRefs.current[id]) {
      translateXRefs.current[id] = new Animated.Value(0);
    }
    return Animated.event(
      [{ nativeEvent: { translationX: translateXRefs.current[id] } }],
      { useNativeDriver: true }
    );
  };

  const onHandlerStateChange = (id: string) => ({ nativeEvent }) => {
    if (!translateXRefs.current[id] || !itemHeightRefs.current[id] || !opacityRefs.current[id]) {
      return;
    }

    if (nativeEvent.oldState === State.ACTIVE) {
      const { translationX } = nativeEvent;

      if (translationX < -100) {
        // Delete threshold reached
        Animated.parallel([
          Animated.timing(itemHeightRefs.current[id], {
            toValue: 0,
            duration: 200,
            useNativeDriver: false
          }),
          Animated.timing(opacityRefs.current[id], {
            toValue: 0,
            duration: 200,
            useNativeDriver: false
          })
        ]).start(() => handleDeleteClass(id));
      } else {
        // Reset position
        Animated.spring(translateXRefs.current[id], {
          toValue: 0,
          useNativeDriver: true,
          bounciness: 8
        }).start();
      }
    }
  };

  const formatTime = (timeString: string) => {
    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':');
    let hour = parseInt(hours);
    
    // Convert to 24-hour format
    if (period === 'PM' && hour !== 12) {
      hour += 12;
    } else if (period === 'AM' && hour === 12) {
      hour = 0;
    }
    
    return `${hour.toString().padStart(2, '0')}:${minutes}`;
  };

  const renderTimeSlot = (slot: TimeSlot) => {
    // Ensure animation values exist
    if (!translateXRefs.current[slot.id]) {
      translateXRefs.current[slot.id] = new Animated.Value(0);
    }
    if (!itemHeightRefs.current[slot.id]) {
      itemHeightRefs.current[slot.id] = new Animated.Value(70);
    }
    if (!opacityRefs.current[slot.id]) {
      opacityRefs.current[slot.id] = new Animated.Value(1);
    }
    if (!listItemAnimations.current[slot.id]) {
      listItemAnimations.current[slot.id] = new Animated.Value(1);
    }

    // Convert times to 24-hour format
    const [startTime, endTime] = slot.time.split(' - ').map(formatTime);
    const timeDisplay = `${startTime} - ${endTime}`;

    const animation = listItemAnimations.current[slot.id];
    const translateX = translateXRefs.current[slot.id];
    const itemHeight = itemHeightRefs.current[slot.id];
    const opacity = opacityRefs.current[slot.id];

    return (
      <Animated.View
        key={slot.id}
        style={[
          styles.classCardContainer,
          {
            height: itemHeight,
            opacity: opacity,
          }
        ]}
      >
        <PanGestureHandler
          onGestureEvent={onGestureEvent(slot.id)}
          onHandlerStateChange={onHandlerStateChange(slot.id)}
          activeOffsetX={[-20, 20]}
          failOffsetY={[-20, 20]}
        >
          <Animated.View
            style={[
              styles.classCard,
              {
                transform: [
                  {
                    translateX: translateX.interpolate({
                      inputRange: [-200, 0],
                      outputRange: [-100, 0],
                      extrapolate: 'clamp',
                    }),
                  },
                  {
                    scale: animation,
                  },
                ],
              },
            ]}
          >
            <View style={styles.cardTouchable}>
              <View style={[styles.timeContainer, { backgroundColor: slot.color || colors.primary }]} >
                <Text style={styles.timeText}>{startTime}</Text>
                <Text style={[styles.durationText, { color: 'rgba(255, 255, 255, 0.8)' }]}>{endTime}</Text>
              </View>
              <View style={styles.classInfo}>
                <Text style={styles.subjectText}>{slot.subject}</Text>
                <View style={styles.detailsRow}>
                  {slot.location && (
                    <View style={styles.detailItem}>
                      <Ionicons name="location-outline" size={16} color={colors.textSecondary} />
                      <Text style={styles.detailText}>{slot.location}</Text>
                    </View>
                  )}
                  {slot.staff && (
                    <View style={styles.detailItem}>
                      <Ionicons name="person-outline" size={16} color={colors.textSecondary} />
                      <Text style={styles.detailText}>{slot.staff}</Text>
                    </View>
                  )}
                </View>
              </View>
            </View>
          </Animated.View>
        </PanGestureHandler>
        <Animated.View
          style={[
            styles.deleteBackground,
            {
              transform: [
                {
                  translateX: translateX.interpolate({
                    inputRange: [-100, 0],
                    outputRange: [0, 100],
                    extrapolate: 'clamp',
                  }),
                },
              ],
              opacity: translateX.interpolate({
                inputRange: [-50, 0],
                outputRange: [1, 0],
                extrapolate: 'clamp',
              }),
            },
          ]}
        >
          <Animated.View
            style={{
              transform: [
                {
                  scale: translateX.interpolate({
                    inputRange: [-100, -50],
                    outputRange: [1, 0.5],
                    extrapolate: 'clamp',
                  }),
                },
              ],
            }}
          >
            <Ionicons name="trash-outline" size={24} color={colors.surface} />
          </Animated.View>
        </Animated.View>
      </Animated.View>
    );
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <View style={styles.greetingContainer}>
            <Text style={styles.greeting}>Schedule</Text>
            <View style={styles.titleContainer}>
              <Text style={styles.headerTitle}>My</Text>
              <Text style={[styles.headerTitle, { color: colors.primary }]}> Timetable</Text>
            </View>
          </View>
          <TouchableOpacity style={styles.addButton} onPress={handleAddClass}>
            <Ionicons name="add-outline" size={24} color={colors.text} />
          </TouchableOpacity>
        </View>
        <View style={styles.headerDecoration} />
      </View>

      {/* Change Course Button */}
      <View style={styles.courseButtonContainer}>
        <TouchableOpacity 
          style={styles.importButton}
          onPress={() => {
            navigation.navigate('CourseSelection' as never);
          }}
        >
          <Ionicons name="school-outline" size={24} color={colors.primary} />
          <Text style={styles.importButtonText}>Select Course</Text>
        </TouchableOpacity>
      </View>

      {/* Days Tab Bar */}
      <View style={styles.tabContainer}>
        {DAYS.map((day) => (
          <TouchableOpacity
            key={day}
            style={[
              styles.tabButton,
              selectedDay === day && styles.selectedTabButton,
            ]}
            onPress={() => onDaySelect(day, DAYS.indexOf(day))}
          >
            <Text
              style={[
                styles.tabButtonText,
                selectedDay === day && styles.selectedTabButtonText,
              ]}
            >
              {day}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Classes List */}
      <ScrollView 
        style={styles.classesContainer} 
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.classesContentContainer}
      >
        {!loading && timetable[selectedDay]?.length === 0 && (
          <View style={styles.emptyState}>
            <Ionicons name="calendar-outline" size={48} color={colors.primary} />
            <Text style={styles.emptyStateTitle}>No Classes</Text>
            <Text style={styles.emptyStateText}>Tap the + button to add a class</Text>
          </View>
        )}
        {timetable[selectedDay]?.map(renderTimeSlot)}
      </ScrollView>

      <ClassEditModal
        visible={showEditModal}
        onClose={() => {
          setShowEditModal(false);
        }}
        onSave={handleSaveClass}
        initialData={null}
        existingClasses={timetable[selectedDay] || []}
        selectedDay={selectedDay}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    position: 'relative',
    height: '100%',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  greetingContainer: {
    flex: 1,
  },
  greeting: {
    fontSize: 16,
    color: colors.textSecondary,
    marginBottom: 4,
    fontWeight: '500',
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: '800',
    color: colors.text,
    letterSpacing: -1,
  },
  headerDecoration: {
    position: 'absolute',
    bottom: 0,
    left: 30,
    right: 30,
    height: 6,
    backgroundColor: colors.primary,
    opacity: 0.15,
    borderRadius: 3,
  },
  addButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(0, 51, 102, 0.05)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  courseButtonContainer: {
    alignItems: 'center',
    paddingVertical: 12,
    backgroundColor: colors.background,
  },
  importButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.cardBackground,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.primary,
    elevation: 2,
    shadowColor: colors.text,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  importButtonText: {
    marginLeft: 8,
    color: colors.primary,
    fontWeight: '600',
    fontSize: 16,
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: '#F3F4F6',
    marginHorizontal: 20,
    marginTop: 20,
    borderRadius: 10,
    padding: 4,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
    borderRadius: 8,
  },
  selectedTabButton: {
    backgroundColor: colors.surface,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 1,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  tabButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.textSecondary,
  },
  selectedTabButtonText: {
    color: colors.text,
    fontWeight: '600',
  },
  classesContainer: {
    flex: 1,
    position: 'relative',
    height: '100%',
  },
  classesContentContainer: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 20,
  },
  classCardContainer: {
    marginBottom: 12,
    position: 'relative',
  },
  classCard: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: 16,
    padding: 12,
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: colors.text,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  deleteBackground: {
    position: 'absolute',
    right: 0,
    height: '100%',
    width: 100,
    backgroundColor: colors.error,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  cardTouchable: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  timeContainer: {
    width: 70,
    height: 70,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 8,
  },
  timeText: {
    color: colors.surface,
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  durationText: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '500',
    marginTop: 2,
    opacity: 0.8,
  },
  classInfo: {
    flex: 1,
    marginLeft: 12,
  },
  subjectText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 4,
  },
  detailsRow: {
    flexDirection: 'column',
    gap: 4,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  detailText: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 60,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.primary,
    marginTop: 12,
  },
  emptyStateText: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: 4,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    margin: 8,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
});

export default TimetableScreen;
